"use client";

import { useEffect, useId, useState, type ReactNode } from "react";
import { testimonialInterval } from "@/utils/testimonials";

export default function QuoteCarousel({
  children,
  label,
  autoplay = false,
  interval,
  navigation = true,
}: {
  children: ReactNode[];
  label: string;
  autoplay?: boolean;
  interval?: number;
  navigation?: boolean;
}) {
  const id = useId();
  const [index, setIndex] = useState(0);
  const [userPlaying, setUserPlaying] = useState<boolean>();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);
  const [hidden, setHidden] = useState(false);
  const count = children.length;
  const active = count ? index % count : 0;
  const intent = autoplay && (userPlaying ?? !reducedMotion);
  const playing = intent && !hovered && !focused && !hidden && count > 1;
  const delay = testimonialInterval(interval);
  const showNavigation = navigation || !intent;

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const motion = () => setReducedMotion(media.matches);
    const visibility = () => setHidden(document.hidden);
    motion();
    visibility();
    media.addEventListener("change", motion);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      media.removeEventListener("change", motion);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, []);

  useEffect(() => {
    if (!playing) return;
    const timer = window.setInterval(
      () => setIndex((current) => (current + 1) % count),
      delay,
    );
    return () => window.clearInterval(timer);
  }, [playing, delay, count]);

  function move(direction: number) {
    setUserPlaying(false);
    setIndex((active + direction + count) % count);
  }
  if (!count) return null;
  return (
    <div
      className="re-testimonial-carousel"
      role="region"
      aria-roledescription="karrusel"
      aria-label={label}
      tabIndex={0}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          setFocused(false);
      }}
      onKeyDown={(event) => {
        if (
          (event.target as HTMLElement).closest(
            "a,input,textarea,select,[contenteditable=true]",
          )
        )
          return;
        if (
          count > 1 &&
          (event.key === "ArrowLeft" || event.key === "ArrowRight")
        ) {
          event.preventDefault();
          move(event.key === "ArrowLeft" ? -1 : 1);
        }
      }}
    >
      <div
        id={id}
        className="re-testimonial-slides"
        aria-live={playing ? "off" : "polite"}
        aria-atomic="true"
      >
        {children.map((child, i) => (
          <div
            key={i}
            role="group"
            aria-roledescription="udtalelse"
            aria-label={`${i + 1} af ${count}`}
            hidden={i !== active}
          >
            {child}
          </div>
        ))}
      </div>
      {count > 1 && (
        <div className="re-testimonial-controls">
          {showNavigation && (
            <>
              <button
                type="button"
                aria-label="Forrige udtalelse"
                aria-controls={id}
                onClick={() => move(-1)}
              >
                ←
              </button>
              <span aria-hidden="true">
                {active + 1} / {count}
              </span>
              <button
                type="button"
                aria-label="Næste udtalelse"
                aria-controls={id}
                onClick={() => move(1)}
              >
                →
              </button>
            </>
          )}
          {autoplay && (
            <button
              type="button"
              aria-controls={id}
              aria-label={
                intent
                  ? "Sæt automatisk skift på pause"
                  : "Start automatisk skift"
              }
              onClick={() => setUserPlaying(!intent)}
            >
              {intent ? "Pause" : "Afspil"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
